<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
  <v-col class="relative" cols="12">
    <v-progress-linear v-if="loading && pagination.currentPage === 1" height="6" color="grey" :indeterminate="true" />
    <v-card
      v-if="!(loading && pagination.currentPage === 1) && items.length"
      class="table-container"
    >
      <v-data-table
        fixed-header
        :headers="headers"
        :sort-by.sync="updatedSortBy"
        :sort-desc.sync="updatedSortDesc"
        :custom-sort="customSort"
        style="max-height: calc(100vh - 176px); overflow-x: none; overflow-y: auto; margin-top: 0px !important; border-radius: 0;"
        :hide-default-header="false"
        :items="items"
        :items-per-page="-1"
        hide-default-footer
        item-key="objectID"
        class="flex-table secondary elevation-1 mt-2 forced-bar-grey"
      >
        <template #item="{ item }">
          <tr :data-id="item.id">
            <td
              v-for="(column, columnNumber) in headers"
              :key="item.uuid+column.attribute"
              :class="{'item-selected': isSelected(item)}"
              style="cursor: pointer"
              @click="action(item)"
              @click.middle="action(item, true)"
              @contextmenu.prevent="showContextMenu($event, item)"
            >
              <div v-if="isFolder(item)">
                <v-row v-if="column.attribute === 'wtitle'">
                  <div style="width: 10px" class="d-flex align-center mx-3">
                    <v-icon class="d-inline-block">folder</v-icon>
                  </div>
                  <v-col cols="10">
                    {{ item['wtitle'] }}
                  </v-col>
                </v-row>
              </div>
              <v-row v-else-if="column.attribute === 'wtitle' || column.isTitle">
                <div style="width: 10px" class="d-flex align-center mx-3">
                  <v-icon class="d-inline-block">description</v-icon>
                </div>
                <v-col cols="10" class="pr-0">
                  <span>{{ item['wtitle'] }}</span>
                </v-col>
              </v-row>

              <SearchStatusDisplay
                v-else-if="column.attribute === 'status'"
                :published="item['publishedAt'] !== null"
                :states="getStates(item)"
                :end-publication-date="item['endPublicationDate']"
                :is-online="item['online']"
                :status="item[column.attribute]"
              />
              <span v-else-if="column.hasOwnProperty('isDate')"><span>{{ new Date(item[column.attribute] * 1000).toLocaleString('fr-FR').substring(0, 10) }}</span></span>
              <span v-else-if="column.subAttributes">
                {{ getObjectItemLabel(item,column.subAttributes) }}
              </span>
              <span v-else>{{ getItemLabel(column.attribute, item) }}</span>
            </td>
          </tr>
        </template>
        <template #body.append>
          <div v-if="!loading && !lastPage" v-observe-visibility="visibilityChanged" style="opacity: 0"><span>...</span></div>
        </template>
      </v-data-table>
    </v-card>

    <v-progress-linear v-if="loading && pagination.currentPage > 1" height="6" color="grey" :indeterminate="true" />

    <v-alert v-if="!loading && count === 0" color="secondary" type="info">
      <span v-if="foldersModeActive">Ce répertoire est vide</span>
      <span v-else>No results</span>
    </v-alert>

    <ContextMenu v-if="foldersModeActive" ref="contextMenu" class="contextMenu" />
  </v-col>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vue2-helpers/vuex';

import SearchStatusDisplay from '@/components/common/SearchStatusDisplay.vue';
import ContextMenu from '@/components/contents/ContextMenu.vue';

import { getWorkflowStates, isFolder, getContentTypeByConfigArg } from '@/utils/content';

import { EventBus } from '@/mixins';

// eslint-disable-next-line no-undef
const props = defineProps({
  items: {
    type: Array,
    default: () => []
  },
  count: {
    type: Number,
    default: 0
  },
  action: {
    type: Function,
    default: () => {}
  },
  showMore: {
    type: Function,
    default: () => {}
  },
  customHeaders: {
    type: Array,
    default: () => []
  },
  pagination: { type: Object, default: () => { } },
  hideCommonHeaders: { type: Boolean },
  foldersModeActive: { type: Boolean },
  loading: {
    type: Boolean,
    default: false
  },
  sortBy: {
    type: String,
    default: undefined
  },
  sortDesc: {
    type: Boolean,
    default: undefined
  },
  useCustomSort: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:sort-by', 'update:sort-desc']);

const customSort = computed(() => (props.useCustomSort ? hits => hits : undefined));

const updatedSortBy = computed({
  get () {
    return props.sortBy;
  },
  set (value) {
    emit('update:sort-by', value);
  }
});

const updatedSortDesc = computed({
  get () {
    return props.sortDesc;
  },
  set (value) {
    emit('update:sort-desc', value);
  }
});
const store = useStore();

const genericHeaders = ref([
  { text: 'Titre', attribute: 'wtitle', align: 'left', value: 'wtitle.raw' },
  { text: 'Type de contenu', attribute: 'wtype', sortable: false }
]);

const commonHeaders = ref([
  { text: 'Dernière modification par', attribute: 'lastUpdateUserName', sortable: false, order: 1000 },
  { text: 'Dernière modification', attribute: 'lastUpdateTime', value: 'lastUpdateTime', isDate: true, order: 1005 },
  { text: 'Statut', attribute: 'status', order: 1010, sortable: false }
]);

const selectedItem = ref(null);

const headers = computed(() => {
  let headersAux = (props.customHeaders.length ? props.customHeaders : genericHeaders.value);

  if (!props.hideCommonHeaders) {
    headersAux = headersAux.concat(commonHeaders.value);
  }

  return headersAux;
});

const lastPage = computed(() => props.pagination.currentPage === props.pagination.totalPages);

const visibilityChanged = isVisible => {
  if (isVisible && !props.loading && !lastPage.value) {
    props.showMore();
  }
};

const contextMenu = ref(null);

// Show context menu and makes the row selected
const showContextMenu = (e, item) => {
  if (props.foldersModeActive) {
    selectedItem.value = item;
    contextMenu.value.show(e, item);
  }
};

// Check if a file is selected
const isSelected = item => {
  if (selectedItem.value && selectedItem.value.uuid === item.uuid) {
    return true;
  }

  return false;
};

const unselectItem = () => {
  selectedItem.value = null;
};

const getStates = content => getWorkflowStates(content, store.state.rawContentTypes, store.state.workflows);

// Item removed event remove from list
const removeItem = item => {
  const index = props.items.findIndex(e => e.uuid === item.uuid);
  if (index !== -1) {
    props.items.splice(index, 1);
  }
};

// Event to add to the list
const addItem = item => {
  props.items.unshift(item);
  selectedItem.value = item;
};

const replaceItem = params => {
  const index = props.items.findIndex(e => e.uuid === params.oldItemId);
  if (index !== -1) {
    props.items.splice(index, 1, params.newItem);
  }
};

const getItemLabel = (value, contents) => {
  const rowContentType = getContentTypeByConfigArg('wtype', contents.wtype, store.state.rawContentTypes);
  const config = rowContentType?.fields[value] || null;
  if (config && config.optionsByReference) {
    const reference = store.getters.getReference(config.optionsByReference);
    return reference[contents[value]] || contents[value];
  }

  return contents[value];
};

const getObjectItemLabel = (item, attributes) => {
  let result = '';
  attributes.forEach((attribute, index) => {
    const keys = attribute.split('.');
    const value = keys.reduce((acc, key) => ((acc && acc[key] !== undefined) ? acc[key] : null), item);
    if (value) {
      result += value;
      // Append '|' as separator only if next value exists and is not the last one
      if (index < keys.length - 1) {
        result += ' | ';
      }
    }
  });
  return result;
};

onMounted(() => {
  // emit('sortByEmit', 'test');
  EventBus.$on('replaceItem', replaceItem);
  EventBus.$on('unselectItem', unselectItem);
  EventBus.$on('removeItem', removeItem);
  EventBus.$on('addItem', addItem);
});

onBeforeUnmount(() => {
  EventBus.$off('replaceItem');
  EventBus.$off('unselectItem');
  EventBus.$off('removeItem');
  EventBus.$off('addItem');
});
</script>

<style>
.flex-table {
  display: flex;
  flex-grow: 1;
  width: 100%;
}
.flex-table > div {
  width: 100%;
}
.table-container {
  display: flex;
  flex-grow: 1;
  overflow: hidden;
}
.theme--dark.v-data-table.v-data-table--fixed-header thead th {
  background-color: #5b5b5c;
}
</style>
